import React, { useContext, useState, useEffect, useRef } from 'react';
import { Typography, Button, CircularProgress, useMediaQuery, TextField, InputAdornment } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../../AuthContext';
import useApi from '../../Api';
import QuestionnaireContext from '../questionnaire/QuestionnaireContext';
import SuggestionsContext from '../explore_page/SuggestionsContext';
import ItinerarySuggestionsContext from '../itinerary/ItinerarySuggestionsContext';
import SuggestionCard from '../explore_page/SuggestionCard';
import { validateTextInput } from '../common/TextValidator';
import '../../styles/components/ChatBot/ChatBot.css';

const StyledHeaderTypography = styled(Typography)(({ theme }) => ({
  fontSize: '1.4rem',
  [theme.breakpoints.down('lg')]: { fontSize: '1.2rem' },
  [theme.breakpoints.down('md')]: { fontSize: '1rem' },
  [theme.breakpoints.down('sm')]: { fontSize: '0.9rem' }
}));

const StyledHeaderButton = styled(Button)(({ theme }) => ({
  fontSize: '1rem',
  padding: '8px 12px',
  [theme.breakpoints.down('lg')]: { fontSize: '0.9rem', padding: '6px 10px' },
  [theme.breakpoints.down('md')]: { fontSize: '0.8rem', padding: '4px 8px' },
  [theme.breakpoints.down('sm')]: { fontSize: '0.7rem', padding: '4px 8px' }
}));

const StyledChatText = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  [theme.breakpoints.down('lg')]: { fontSize: '0.9rem' },
  [theme.breakpoints.down('md')]: { fontSize: '0.8rem' },
  [theme.breakpoints.down('sm')]: { fontSize: '0.7rem' }
}));

const ChatBot = ({
  onOpenPreferences,
  toggleMapVisibility,
  isMapVisible,
  suggestionEndpoint = '/explore/suggestions',
  title = 'ChatBot',
  titleSectionText = 'Find Your Next Adventure!',
  useItineraryContext = false,
  origin_id,
  itinerary_id,
  subscriptionRequirement
}) => {
  const { answers } = useContext(QuestionnaireContext);
  const itineraryContext = useContext(ItinerarySuggestionsContext);
  const exploreContext = useContext(SuggestionsContext);
  const { suggestions, setSuggestions } = useItineraryContext ? itineraryContext : exploreContext;
  const { token, user } = useAuth();
  const apiClient = useApi();
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const maxChars = 500;
  const minChars = 0;
  const [inputErrors, setInputErrors] = useState([]);
  const [inputTouched, setInputTouched] = useState(false);
  const colors = ['#FF5733', '#33FF57', '#3357FF', '#FF33A1', '#ecf241'];
  const defaultColor = '#800080';
  const chatHistoryRef = useRef(null);
  const endOfMessagesRef = useRef(null);
  const isMobile = useMediaQuery('(max-width:1024px)');
  const hasSuggestions = suggestions && suggestions.length > 0;

  const initialText = suggestionEndpoint === '/itinerary/suggestions'
    ? 'Let us help you plan your adventure, please update your travel preferences! These will be used to make suggestions and saved to your profile.'
    : 'Let us help you find your next adventure, please update your travel preferences! These will be used to make suggestions and saved to your profile.';

  const initialMessages = suggestions && suggestions.length > 0
    ? [{
        sender: 'bot',
        suggestions: suggestions,
        timestamp: new Date().toLocaleTimeString()
      }]
    : [{
        sender: 'bot',
        initial: true,
        text: initialText,
        timestamp: new Date().toLocaleTimeString()
      }];

  const [messages, setMessages] = useState(initialMessages);

  useEffect(() => {
    if (!inputTouched) return;
    const validation = validateTextInput(input, {
      maxLength: maxChars,
      minLength: minChars,
      disallowLinks: true
    });
    setInputErrors(validation.errors);
  }, [input, inputTouched, maxChars]);

  const defaultRequirement = suggestionEndpoint === '/itinerary/suggestions'
    ? 'premium'
    : suggestionEndpoint === '/explore/suggestions'
      ? 'support'
      : 'none';

  const requirement = subscriptionRequirement ?? defaultRequirement;

  const handleGetRecommendations = async () => {
    if (loading) return;
    if (requirement === 'support' && !(user?.isSupport || user?.isPremium)) {
      setMessages(prev => [
        ...prev,
        { sender: 'bot', text: 'Access denied: This feature requires a subscription.', timestamp: new Date().toLocaleTimeString() }
      ]);
      return;
    }
    if (requirement === 'premium' && !user?.isPremium) {
      setMessages(prev => [
        ...prev,
        { sender: 'bot', text: 'Access denied: This feature requires a Premium subscription.', timestamp: new Date().toLocaleTimeString() }
      ]);
      return;
    }
    const validation = validateTextInput(input, {
      maxLength: maxChars,
      minLength: minChars,
      disallowLinks: true
    });
    if (!validation.isValid) return;
    const userMessage = { sender: 'user', text: input, timestamp: new Date().toLocaleTimeString() };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    const messagesToConsider = updatedMessages.slice(0, updatedMessages.length - 1);
    let totalUserCount = 0;
    let totalSuggestionCount = 0;
    messagesToConsider.forEach(msg => {
      if (msg.initial) return;
      if (msg.sender === 'user') totalUserCount++;
      else if (msg.suggestions || msg.suggestion) totalSuggestionCount++;
    });
    let userCounter = 0;
    let suggestionCounter = 0;
    const olderEntries = [];
    messagesToConsider.forEach(msg => {
      if (msg.initial) return;
      if (msg.sender === 'user') {
        userCounter++;
        if (userCounter > totalUserCount - 1) {
          olderEntries.push(`User: ${msg.text}`);
        }
      } else if (msg.suggestions || msg.suggestion) {
        suggestionCounter++;
        if (suggestionCounter > totalSuggestionCount - 1) {
          if (msg.suggestions) {
            olderEntries.push(`Bot suggestions: ${msg.suggestions.map(s => s.suggestion_name).join(', ')}`);
          } else {
            olderEntries.push(`Bot suggestion: ${msg.suggestion.suggestion_name}`);
          }
        }
      }
    });
    const finalUserPrompt = `User (highest priority): ${userMessage.text}`;
    const finalChatHistory = [...olderEntries, finalUserPrompt];
    const previousSuggestions = updatedMessages.reduce((acc, msg) => {
      if (msg.suggestion) {
        acc.push(msg.suggestion.suggestion_name);
      } else if (msg.suggestions) {
        acc.push(...msg.suggestions.map(sug => sug.suggestion_name));
      }
      return acc;
    }, []);
    const limitedPreviousSuggestions = previousSuggestions.slice(-20);
    setInput('');
    setInputTouched(false);
    setLoading(true);
    try {
      const data = {
        ...answers,
        origin_id: origin_id,
        itinerary_id: itinerary_id,
        chat_history: finalChatHistory,
        currentSuggestions: limitedPreviousSuggestions,
        travelDates: answers.travelDates || { startDate: null, endDate: null }
      };
      const response = await apiClient.post(suggestionEndpoint, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fetchedSuggestions = response.data.suggested_places || [];
      const coloredSuggestions = fetchedSuggestions.map((suggestion, index) => ({
        ...suggestion,
        markerColor: colors[index % colors.length]
      }));
      let transformedSuggestions = coloredSuggestions;
      if (useItineraryContext) {
        transformedSuggestions = coloredSuggestions.map(sug => ({
          ...sug,
          suggestion_name: sug.name,
          suggestion_summary: sug.summary,
          suggestion_latitude: sug.latitude,
          suggestion_longitude: sug.longitude,
          origin_id: origin_id ? origin_id.toString() : '0',
          itinerary_id: itinerary_id || ''
        }));
      }
      setMessages(prev =>
        prev.map(msg => {
          if (msg.suggestion) {
            return { ...msg, suggestion: { ...msg.suggestion, markerColor: defaultColor } };
          }
          if (msg.suggestions) {
            return { ...msg, suggestions: msg.suggestions.map(sug => ({ ...sug, markerColor: defaultColor })) };
          }
          return msg;
        })
      );
      setSuggestions(transformedSuggestions);
      const aggregatedBotMessage = {
        sender: 'bot',
        suggestions: transformedSuggestions,
        timestamp: new Date().toLocaleTimeString()
      };
      setMessages(prev => [...prev, aggregatedBotMessage]);
    } catch (error) {
      setMessages(prev => [
        ...prev,
        { sender: 'bot', text: 'Sorry, there was an error fetching suggestions.', timestamp: new Date().toLocaleTimeString() }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearChat = () => {
    setMessages([{
      sender: 'bot',
      initial: true,
      text: initialText,
      timestamp: new Date().toLocaleTimeString()
    }]);
    setSuggestions([]);
  };

  useEffect(() => {
    if (endOfMessagesRef.current) {
      endOfMessagesRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  return (
    <div className="chatbot chatbot--open">
      <div className="chatbot__header">
        <StyledHeaderTypography>{title}</StyledHeaderTypography>
        <div className="chatbot__header-buttons">
          <StyledHeaderButton variant="outlined" color="inherit" onClick={handleClearChat}>
            Clear Chat
          </StyledHeaderButton>
          <StyledHeaderButton variant="outlined" color="inherit" onClick={onOpenPreferences}>
            Update Travel Preferences
          </StyledHeaderButton>
          {toggleMapVisibility && isMobile && (
            <StyledHeaderButton variant="outlined" color="inherit" onClick={toggleMapVisibility} disabled={!hasSuggestions}>
              {isMapVisible ? 'Hide Map' : 'Show Map'}
            </StyledHeaderButton>
          )}
        </div>
      </div>
      <div className="chatbot__body">
        <div className="chatbot__content">
          <div className="chatbot__title-section">
            <Typography variant="h3" className="chatbot__main-title" sx={{ fontSize: { xs: '1rem', sm: '1.5rem', md: '2rem', lg: '2.5rem', xl: '3rem' } }}>
              {titleSectionText}
            </Typography>
          </div>
          <div className="chatbot__history" ref={chatHistoryRef} role="log" aria-live="polite">
            {messages.map((msg, index) =>
              msg.initial ? (
                <div key={index} className="chatbot__message chatbot__message--bot">
                  <StyledChatText>{msg.text}</StyledChatText>
                  <Typography variant="caption" className="chatbot__message-timestamp">
                    {msg.timestamp}
                  </Typography>
                  <Button variant="outlined" color="inherit" onClick={onOpenPreferences} className="chatbot__update-btn">
                    Update Travel Preferences
                  </Button>
                </div>
              ) : msg.suggestions ? (
                <div key={index} className="chatbot__message chatbot__message--bot">
                  {msg.suggestions.map((suggestion, idx) => (
                    <SuggestionCard key={idx} suggestion={suggestion} markerColor={suggestion.markerColor} />
                  ))}
                  <Typography variant="caption" className="chatbot__message-timestamp">
                    {msg.timestamp}
                  </Typography>
                </div>
              ) : msg.suggestion ? (
                <div key={index} className="chatbot__message chatbot__message--bot">
                  <SuggestionCard suggestion={msg.suggestion} markerColor={msg.suggestion.markerColor} />
                  {msg.timestamp && (
                    <Typography variant="caption" className="chatbot__message-timestamp">
                      {msg.timestamp}
                    </Typography>
                  )}
                </div>
              ) : (
                <div key={index} className={`chatbot__message chatbot__message--${msg.sender}`}>
                  <StyledChatText>{msg.text || <i>(No message)</i>}</StyledChatText>
                  {msg.timestamp && (
                    <Typography variant="caption" className="chatbot__message-timestamp">
                      {msg.timestamp}
                    </Typography>
                  )}
                </div>
              )
            )}
            <div ref={endOfMessagesRef} />
          </div>
        </div>
        <div className="chatbot__chat" role="form">
          <div className="chatbot__input-container">
            <TextField
              fullWidth
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                if (!inputTouched) setInputTouched(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleGetRecommendations();
                }
              }}
              placeholder="Type your message..."
              error={inputErrors.length > 0}
              helperText={inputErrors.length > 0 ? inputErrors.join(' ') : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {`${input.length}/${maxChars}`}
                  </InputAdornment>
                )
              }}
              inputProps={{ maxLength: maxChars, "aria-label": "Chat message input" }}
            />
            <Button
              className="chatbot__submit-button"
              variant="contained"
              color="primary"
              size="small"
              onClick={handleGetRecommendations}
              disabled={loading || inputErrors.length > 0}
              sx={{
                alignSelf: 'stretch',
                padding: '0 10px',
                minWidth: 'auto'
              }}
            >
              {loading ? <CircularProgress size={24} /> : 'Search'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatBot;
